// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { lncbBootHtml, useEffect, useRef, useState } from "./00-boot.jsx";
import { langPick } from "./01-taxonomy-cities.jsx";
import { ACTIVE_LANG, T, tx } from "./04-i18n-dict.jsx";
import { Av, cssAlpha, lanacBuzz } from "./05-ui-atoms-theme.jsx";
import { UiIcon } from "./05-ui-atoms-theme.jsx";   // (09/10) le pictogramme de l'aperçu « Photo » — sur sa ligne : celle du dessus est tenue mot pour mot par un banc
import { mediaUrl } from "./06-images-upload.jsx";
// `textColor` (optionnel) : variante LISIBLE du même accent, pour le libellé quand la pastille
// est active. Mesuré le 01/09/2026 : l'or sur son propre fond à 13 % donnait 4,24:1 = échec AA.
// La BORDURE garde `color` (composant non textuel : seuil 3:1, tenu). Sans `textColor`, le
// comportement est strictement celui d'avant.
function Pill({active,color,label,onClick,textColor}){return <button type="button" onClick={onClick} style={{padding:"8px 20px",borderRadius:20,border:`2px solid ${active?color:"var(--color-border)"}`,background:active?cssAlpha(color,13):"transparent",color:active?(textColor||color):"var(--color-text-secondary)",fontWeight:"700",fontSize:13,cursor:"pointer"}}>{label}</button>;}
// `error` + `anchor` (vague 2 du dépôt, 01/09/2026) : le message d'erreur vit SOUS le champ
// qu'il décrit — pas dans un bandeau à l'autre bout d'un panneau 100dvh. `anchor` donne
//   • data-pfld : la cible des liens du résumé d'erreurs (scroll + focus) ;
//   • perr-<anchor> : l'id que le contrôle référence via aria-describedby (posé PAR L'APPELANT,
//     explicitement — pas de cloneElement « malin » : plusieurs Fld enveloppent des fragments
//     ou plusieurs contrôles, cloner « le premier enfant » y serait fragile).
// Sans ces deux props, rendu strictement identique à avant.
function Fld({label,children,full,error,anchor}){return <div data-pfld={anchor||undefined} style={{gridColumn:full?"1/-1":"auto",display:"flex",flexDirection:"column",gap:4}}><label style={{fontSize:11,color:"var(--color-text-secondary)",letterSpacing:1,textTransform:"uppercase",fontWeight:"700"}}>{label}</label>{children}{error&&<div id={anchor?("perr-"+anchor):undefined} style={{fontSize:11.5,color:"var(--danger-text)",fontWeight:"700",lineHeight:1.45}}>{error}</div>}</div>;}

// -------------------------------------------------------------------------------
// VIDEO PLAYER — force muted via DOM ref (React JSX muted bug workaround)
// -------------------------------------------------------------------------------
function VideoPlayer({src,poster,style,controls=false,vref,...props}){
  const tryPlay=e=>{const v=e.target;v.play().catch(()=>{});};
  return <video
    ref={v=>{if(v&&!v.dataset.lanacMutedInit){v.muted=true;v.dataset.lanacMutedInit="1";}if(vref)vref(v);}}
    src={src} poster={poster}
    autoPlay loop playsInline preload="auto"
    controls={controls}
    style={style}
    onLoadedMetadata={tryPlay}
    onCanPlay={tryPlay}
    {...props}
  />;
}
// LncVideo — le MÊME blur-up « focus » que LncImg, appliqué au POSTER de la vidéo (cartes grille + rails).
// Vignette floue (poster 96px) + shimmer, puis la vidéo fait la mise au point quand sa 1re image est prête.
//   • Révélation via onLoadedData → événement LIBRE, n'écrase PAS le onCanPlay qui lance l'autoplay (la
//     logique de lecture de VideoPlayer — autoplay/loop/muet — reste STRICTEMENT intacte).
//   • Filet de sécurité 1,5 s → la vidéo se révèle même si l'événement ne se déclenche jamais (ex. codec
//     .mov selon le navigateur) → JAMAIS de vidéo bloquée invisible.
//   • Garde 1 : pas de vraie vignette CDN (poster absent / externe / base64) → VideoPlayer simple, comme avant.
//   • Conteneurs de carte en overflow:hidden → la couche floue est découpée (pas de bandeau, cf. bug fiche).
// (08/10/2026) UNE VIDÉO QUI ÉCHOUE. AVANT : son aperçu flou en échec était remplacé par le pictogramme de la coquille —
//  montré FLOU, sous une vidéo vide — et rien n'était redemandé ; le bouton du son restait posé sur une image morte.
//   • l'aperçu flou porte la marque « soi » (la coquille n'y touche plus) et SE RETIRE s'il échoue ;
//   • la vidéo en échec (fichier absent, réseau coupé, format refusé) est redemandée UNE fois, après 3 s ; un nouvel échec :
//     elle est MORTE, et le dit à qui l'a posée (`surMorte`) — la carte montre alors la PHOTO de l'annonce (LncImg, qui sait
//     se redemander) et retire le bouton du son. Sans `surMorte` (la fiche), la balise reste, avec son affiche, comme avant.
//   Un échec par DEMANDE (plusieurs évènements d'erreur de la même balise n'en font qu'un). Minuteur rendu au démontage.
const VIDEO_REESSAIS=1, VIDEO_REESSAI_MS=3000;
function LncVideo({src,poster,w,style,vref,surMorte,...rest}){
  const tiny=mediaUrl(poster,{w:96});
  const full=mediaUrl(poster,{w});
  const [ready,setReady]=useState(false);
  const [essai,setEssai]=useState(0);          // la demande en cours (0, puis 1 : redemandée)
  const [echecs,setEchecs]=useState(0);        // demandes en échec
  const [flouParti,setFlouParti]=useState(false); // l'aperçu flou a échoué : retiré
  const t0=useRef(Date.now());
  const done=useRef(false);
  const reveal=()=>{ if(done.current)return; done.current=true;
    const wait=Math.max(0,180-(Date.now()-t0.current));
    if(wait>0) setTimeout(()=>setReady(true),wait); else setReady(true);
  };
  useEffect(()=>{ const id=setTimeout(reveal,1500); return()=>clearTimeout(id); },[]); // filet anti-blocage
  useEffect(()=>{
    if(!echecs) return;
    if(echecs>VIDEO_REESSAIS){ if(surMorte) surMorte(); return; }
    const t=setTimeout(()=>setEssai(echecs),VIDEO_REESSAI_MS);
    return()=>clearTimeout(t);
  },[echecs]);
  const surErreur=()=>setEchecs(n=>Math.max(n,essai+1));
  if(!poster||tiny===full){ // Garde 1 → VideoPlayer simple (rendu identique à aujourd'hui)
    return <VideoPlayer key={"v"+essai} src={src} poster={full} style={style} vref={vref} onError={surErreur} {...rest}/>;
  }
  return <>
    {!flouParti&&<img src={tiny} alt="" aria-hidden="true" loading="lazy" decoding="async" data-lnc-fb="soi" onError={()=>setFlouParti(true)} className="lnc-img-layer lnc-img-blur"/>}
    {!ready&&<div className="lnc-img-shim" aria-hidden="true"/>}
    <VideoPlayer key={"v"+essai} src={src} poster={full} vref={vref} style={style} className={"lnc-img-layer lnc-img-full"+(ready?" is-loaded":"")} onLoadedData={reveal} onError={surErreur} {...rest}/>
  </>;
}

// -------------------------------------------------------------------------------
// ITEM CARD
// -------------------------------------------------------------------------------
/* ===== Btn — source unique de style boutons (Phase 0, non utilisé encore) =====
   Mappe variant/size/état -> classes .l-btn ; transmet toutes les autres props
   (onClick, type, aria-*, disabled…) intactes. Aucune logique métier. */
function Btn({variant="primary",size="md",full=false,loading=false,disabled=false,className="",style,children,...rest}){
  const cls=["l-btn","l-btn--"+variant];
  if(size!=="md") cls.push("l-btn--"+size);
  if(full) cls.push("l-btn--full");
  if(loading) cls.push("is-loading");
  if(className) cls.push(className);
  return <button type="button" className={cls.join(" ")} disabled={disabled||loading} aria-busy={loading||undefined} style={style} {...rest}>{children}</button>;
}

/* ===== IconBtn — boutons icônes cohérents (Phase 0, non utilisé encore) =====
   Taille via prop size ; tone default/brand/danger/onmedia. Transmet onClick/aria-* intacts. */
function IconBtn({size=40,tone="default",className="",style,children,...rest}){
  const cls=["l-iconbtn"];
  if(tone!=="default") cls.push("l-iconbtn--"+tone);
  if(className) cls.push(className);
  return <button type="button" className={cls.join(" ")} style={{width:size,height:size,...style}} {...rest}>{children}</button>;
}

/* ===== AppSheet — popup unifié premium (lot 1 : partage + signalement) =====
   Bottom-sheet sur mobile / modale centrée sur desktop (bascule CSS 769px, styles .lnc-sheet-* dans mobile-nav.css).
   Monté conditionnellement par le parent ({cond&&<AppSheet…>}). Toute fermeture interne (fond, ✕, Échap) joue
   l'animation de sortie PUIS appelle onClose. Pour qu'un bouton du contenu ferme avec animation, passer
   children en fonction : {close=><button onClick={close}>…</button>}. zIndex : chaque usage GARDE son z-index
   historique (aucun réordonnancement de la pile existante). */
let __lncSheetLocks=0,__lncSheetPadPrev="";
const __lncSheetStack=[];
function lncSheetLock(){
  if(++__lncSheetLocks>1) return;
  // Compense la disparition de la scrollbar desktop (évite le décalage de mise en page).
  const gap=window.innerWidth-document.documentElement.clientWidth;
  __lncSheetPadPrev=document.body.style.paddingRight;
  if(gap>0) document.body.style.paddingRight=gap+"px";
  document.documentElement.classList.add("lnc-sheet-lock"); // <html> est le scroller réel du document
  document.body.classList.add("lnc-sheet-lock");
}
function lncSheetUnlock(){
  __lncSheetLocks=Math.max(0,__lncSheetLocks-1);
  if(__lncSheetLocks>0) return;
  document.documentElement.classList.remove("lnc-sheet-lock");
  document.body.classList.remove("lnc-sheet-lock");
  document.body.style.paddingRight=__lncSheetPadPrev;
}
//  (02/10, lot B) `ancre="haut"` : la fenêtre se pose sous l'en-tête au lieu du centre — pour une feuille qui porte un champ de
//  recherche : le clavier du téléphone couvre la moitié basse de l'écran sans le rétrécir, les résultats restent au-dessus.
function AppSheet({onClose,title,zIndex=2400,maxWidth,lang="fr",ariaLabel,ancre=null,children}){
  const [closing,setClosing]=useState(false);
  const sheetRef=useRef(null);
  const doneRef=useRef(false);
  const closeTimer=useRef(null);
  const finish=()=>{if(doneRef.current)return;doneRef.current=true;onClose&&onClose();};
  const requestClose=()=>{
    if(doneRef.current)return;
    if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches){finish();return;}
    setClosing(true);
    closeTimer.current=setTimeout(finish,320); // garde-fou si animationend ne vient jamais
  };
  useEffect(()=>{
    const prevFocus=document.activeElement;
    const id={};
    __lncSheetStack.push(id);
    lncSheetLock();
    lanacBuzz(8);
    sheetRef.current&&sheetRef.current.focus({preventScroll:true});
    const onKey=e=>{
      if(__lncSheetStack[__lncSheetStack.length-1]!==id) return; // seul le sheet du dessus réagit
      if(e.key==="Escape"){e.stopPropagation();requestClose();return;}
      if(e.key==="Tab"&&sheetRef.current){
        const els=Array.prototype.slice.call(sheetRef.current.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')).filter(el=>!el.disabled);
        if(!els.length) return;
        const first=els[0],last=els[els.length-1];
        if(e.shiftKey&&(document.activeElement===first||document.activeElement===sheetRef.current)){e.preventDefault();last.focus();}
        else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}
      }
    };
    document.addEventListener("keydown",onKey,true);
    return()=>{
      document.removeEventListener("keydown",onKey,true);
      const i=__lncSheetStack.indexOf(id);
      if(i>=0)__lncSheetStack.splice(i,1);
      lncSheetUnlock();
      closeTimer.current&&clearTimeout(closeTimer.current);
      prevFocus&&prevFocus.focus&&document.contains(prevFocus)&&prevFocus.focus({preventScroll:true});
    };
  },[]);
  return <div className={"lnc-sheet-backdrop"+(ancre==="haut"?" lnc-sheet-backdrop--haut":"")+(closing?" lnc-sheet-backdrop--closing":"")} style={{zIndex}}
    onClick={e=>e.target===e.currentTarget&&requestClose()}
    onAnimationEnd={e=>{closing&&e.target===sheetRef.current&&finish();}}>
    <div ref={sheetRef} className="lnc-sheet" style={maxWidth?{maxWidth}:null} role="dialog" aria-modal="true"
      aria-label={ariaLabel||(typeof title==="string"?title:undefined)} tabIndex={-1} onClick={e=>e.stopPropagation()}>
      <div className="lnc-sheet-grip" aria-hidden="true"/>
      <button type="button" className="lnc-sheet-close" onClick={requestClose} aria-label={tx("tax.fermer_b89077",lang)}>✕</button>
      {title&&<div className="lnc-sheet-title">{title}</div>}
      <div className="lnc-sheet-body">{typeof children==="function"?children(requestClose):children}</div>
    </div>
  </div>;
}

/* ===== Barre de page d'un écran de téléphone (« ‹ Titre · action ») =====
   Audit mobile 30/09/2026 (lot 3) : déplacée ici depuis 22-nav-error-bootstrap, à l'octet près, pour que le profil (18) la
   rende lui-même — une section ouverte y remplace le titre et sa flèche ferme la section — sans importer le montage de
   l'application. C'est LA seule barre d'un écran à barre de page : la barre logo/cœur est cachée sur ces vues (CSS lot 3). */
function MobilePageHeader({title,onBack,action,lang=ACTIVE_LANG}){
  return <div className="lanac-mobile-page-top">
    <button className="lanac-mobile-back" onClick={onBack} aria-label={T[lang].ui.back}>‹</button>
    <div className="lanac-mobile-page-title">{title}</div>
    <div className="lanac-mobile-page-action">{action||null}</div>
  </div>;
}

/* ===== Confirm / Alert global premium (lot 2) — API impérative =====
   lncConfirm(opts)->Promise<bool>, lncAlert(opts)->Promise<void>. Adossé à un
   <LncDialogHost/> UNIQUE monté à la racine (sibling de <App/>), toujours présent
   quel que soit l'écran (splash / auth / app). Aucune prop à threader : n'importe
   quel composant peut demander une confirmation. File d'attente si plusieurs
   demandes se chevauchent (rare — les dialogues sont modaux). Remplace les
   window.confirm / window.alert natifs (non stylables, datés sur mobile).
   opts: {message, title?, tone?:'default'|'danger'|'success', confirmLabel?, cancelLabel?, okLabel?, pre?} */
let __lncDialogPush=null;                 // enregistré par l'hôte (useEffect)
let __lncDialogSeq=0;
const __lncPlain=(m)=>typeof m==="string"?m:"";
function __lncDialogDefaults(){
  // ACTIVE_LANG : miroir global de la langue (déjà maj à chaque rendu de App). langPick retombe dessus par défaut.
  const l=ACTIVE_LANG;
  return {
    confirm:tx("tax.confirmer_7ce0c0",l),
    cancel:tx("tax.annuler_64544e",l),
    ok:tx("tax.ok_45a40c",l)
  };
}
function lncConfirm(opts){
  const o=typeof opts==="string"?{message:opts}:(opts||{});
  const d=__lncDialogDefaults();
  const req={kind:"confirm",id:++__lncDialogSeq,tone:o.tone||"default",title:o.title||"",message:o.message||"",
    confirmLabel:o.confirmLabel||d.confirm,cancelLabel:o.cancelLabel||d.cancel};
  return new Promise(resolve=>{
    req.resolve=resolve;
    if(__lncDialogPush) __lncDialogPush(req);
    else resolve(typeof window!=="undefined"&&window.confirm?window.confirm(__lncPlain(req.message)):false); // filet : hôte non monté
  });
}
function lncAlert(opts){
  const o=typeof opts==="string"?{message:opts}:(opts||{});
  const d=__lncDialogDefaults();
  const req={kind:"alert",id:++__lncDialogSeq,tone:o.tone||"default",title:o.title||"",message:o.message||"",
    okLabel:o.okLabel||d.ok,pre:!!o.pre};
  return new Promise(resolve=>{
    req.resolve=()=>resolve();
    if(__lncDialogPush) __lncDialogPush(req);
    else { if(typeof window!=="undefined"&&window.alert) window.alert(__lncPlain(req.message)); resolve(); }
  });
}
const LNC_DIALOG_ICONS={
  danger:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>,
  success:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>,
  info:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9.5"/><path d="M12 11v5"/><path d="M12 7.5h.01"/></svg>,
  default:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M9.2 9a2.9 2.9 0 0 1 5.7.9c0 1.9-2.9 2.8-2.9 2.8"/><path d="M12 16.5h.01"/><circle cx="12" cy="12" r="9.5"/></svg>
};
function LncDialogCard({req,onSettle}){
  const [closing,setClosing]=useState(false);
  const cardRef=useRef(null);
  const doneRef=useRef(false);
  const timerRef=useRef(null);
  const isAlert=req.kind==="alert";
  const tone=req.tone==="danger"?"danger":req.tone==="success"?"success":"default";
  const iconKey=isAlert&&tone==="default"?"info":tone;
  // Valeur de résolution EN ATTENTE : posée par close(val), relue par le timeout ET par onAnimationEnd.
  // Indispensable — sinon onAnimationEnd (fin de l'anim de sortie) résoudrait une valeur codée en dur et
  // écraserait le vrai choix de l'utilisateur (ex. « Confirmer » résolu en false). doneRef garantit une seule résolution.
  const pendingValRef=useRef(isAlert?undefined:false);
  // AUDIT 2026-07-24 (Phase 2) : on retient si la fermeture vient d'un CHOIX EXPLICITE
  //  (bouton) ou d'un geste IMPLICITE (Échap). Une confirmation fermée sans choix rend
  //  `false` ⇒ l'appelant fait `return` et l'écran ne bouge pas. L'hôte doit pouvoir le
  //  DIRE (toast) plutôt que laisser croire que le geste a échoué.
  const pendingImplicitRef=useRef(false);
  const finish=(val)=>{if(doneRef.current)return;doneRef.current=true;onSettle(val,pendingImplicitRef.current);};
  const close=(val,implicit)=>{
    if(doneRef.current)return;
    pendingValRef.current=val;
    pendingImplicitRef.current=!!implicit;
    if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches){finish(val);return;}
    setClosing(true);
    timerRef.current=setTimeout(()=>finish(pendingValRef.current),320); // garde-fou si animationend absent
  };
  useEffect(()=>{
    const prevFocus=document.activeElement;
    lncSheetLock(); // réutilise le verrou de scroll du lot 1 (compteur partagé)
    lanacBuzz(tone==="danger"?[7,32,7]:8);
    // Focus sur le bouton le plus sûr : Annuler pour un dialogue destructif, sinon l'action principale.
    const sel=tone==="danger"&&!isAlert?"[data-lnc-cancel]":"[data-lnc-confirm]";
    const el=cardRef.current&&cardRef.current.querySelector(sel);
    el&&el.focus({preventScroll:true});
    const onKey=(e)=>{
      if(e.key==="Escape"){e.stopPropagation();e.preventDefault();close(isAlert?undefined:false,true);return;}
      if(e.key==="Enter"&&isAlert){e.preventDefault();close(undefined);return;}
      if(e.key==="Tab"&&cardRef.current){
        const f=Array.prototype.slice.call(cardRef.current.querySelectorAll("button")).filter(b=>!b.disabled);
        if(!f.length)return;
        const first=f[0],last=f[f.length-1];
        if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();}
        else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}
      }
    };
    document.addEventListener("keydown",onKey,true);
    return()=>{
      document.removeEventListener("keydown",onKey,true);
      lncSheetUnlock();
      timerRef.current&&clearTimeout(timerRef.current);
      prevFocus&&prevFocus.focus&&document.contains(prevFocus)&&prevFocus.focus({preventScroll:true});
    };
  },[]);
  return <div className={"lnc-cd-backdrop"+(closing?" lnc-cd-backdrop--closing":"")} style={{zIndex:9600}}
    onClick={e=>{
      if(e.target!==e.currentTarget) return;
      // AUDIT 2026-07-24 (Phase 2) — un CONFIRM exige désormais un choix EXPLICITE : le
      //  fond ne le ferme plus (motif « alerte » iOS / dialogue critique Material 3).
      //  C'est CE geste — le tap légèrement à côté de la carte, sur mobile — qui annulait
      //  des actions EN SILENCE (bug « je clique et rien ne s'affiche »). ⚠️ Le TON ne
      //  suffit pas à décider qui protéger : « Confirmer achat » / « Confirmer vente »
      //  sont en ton `default` et pourtant IRRÉVOCABLES ⇒ on protège TOUS les confirms.
      //  Un ALERT (bouton unique, informatif) reste fermable au fond : l'y fermer
      //  ÉQUIVAUT à « OK », aucune action n'est perdue. Échap reste actif partout
      //  (exigence ARIA APG pour un dialogue modal) — et il est, lui, tracé (toast).
      if(!isAlert) return;
      close(undefined,true);
    }}
    onAnimationEnd={e=>{closing&&e.target===cardRef.current&&finish(pendingValRef.current);}}>
    <div ref={cardRef} className={"lnc-cd lnc-cd--"+tone} role={isAlert?"alertdialog":"dialog"} aria-modal="true"
      aria-label={typeof req.title==="string"&&req.title?req.title:undefined} tabIndex={-1} onClick={e=>e.stopPropagation()}>
      <div className="lnc-cd-ic" aria-hidden="true">{LNC_DIALOG_ICONS[iconKey]||LNC_DIALOG_ICONS.default}</div>
      {req.title&&<div className="lnc-cd-title">{req.title}</div>}
      <div className={"lnc-cd-msg"+(req.pre?" lnc-cd-msg--pre":"")}>{req.message}</div>
      <div className={"lnc-cd-actions"+(isAlert?" lnc-cd-actions--single":"")}>
        {!isAlert&&<button type="button" data-lnc-cancel className="lnc-cd-btn lnc-cd-btn--cancel" onClick={()=>close(false)}>{req.cancelLabel}</button>}
        <button type="button" data-lnc-confirm className="lnc-cd-btn lnc-cd-btn--confirm" onClick={()=>close(isAlert?undefined:true)}>{isAlert?req.okLabel:req.confirmLabel}</button>
      </div>
    </div>
  </div>;
}
function LncDialogHost(){
  const qRef=useRef([]);
  const [,setTick]=useState(0);
  // AUDIT 2026-07-24 (Phase 2) : toast « Action annulée ». Rendu par l'hôte LUI-MÊME
  //  (déjà monté à la racine, sibling de <App/>) ⇒ aucune plomberie nouvelle, aucun
  //  câblage dans App, et le même HUD accessible (role=status, aria-live=polite) que
  //  le reste de l'app. Portée volontairement étroite : voir `settle` ci-dessous.
  const [cancelToast,setCancelToast]=useState(null);
  const toastSeqRef=useRef(0);
  const bump=()=>setTick(t=>t+1);
  useEffect(()=>{
    __lncDialogPush=(req)=>{qRef.current=[...qRef.current,req];bump();};
    return()=>{__lncDialogPush=null;};
  },[]);
  const current=qRef.current[0]||null;
  const settle=(val,implicit)=>{
    const head=qRef.current[0];
    qRef.current=qRef.current.slice(1);
    head&&head.resolve(val);
    // Une CONFIRMATION fermée SANS choix (Échap) fait `return` chez l'appelant : l'écran
    //  ne bouge pas, aucun message n'est écrit. On le DIT — plus jamais de non-réponse
    //  muette. Un clic explicite sur « Annuler » ne déclenche RIEN (l'utilisateur sait
    //  déjà ce qu'il a fait ; un toast y serait du bruit), pas plus qu'un alert fermé.
    if(head&&head.kind==="confirm"&&implicit&&val===false){
      toastSeqRef.current+=1;
      setCancelToast({msg:tx("ui.actionCancelled",ACTIVE_LANG),color:"var(--color-info)",id:toastSeqRef.current});
    }
    bump();
  };
  return <React.Fragment>
    {current&&<LncDialogCard key={current.id} req={current} onSettle={settle}/>}
    <LncToastHud req={cancelToast} onDone={id=>setCancelToast(t=>(t&&t.id===id?null:t))}/>
  </React.Fragment>;
}

/* (LncToastHud — le message de l'application posé par notify() — est écrit plus bas, après le petit mot dont il porte
   l'habit depuis le 08/10 : il n'y a plus de carte au milieu de l'écran.) */

/* ===== LncMot — LE PETIT MOT, LE MÊME PARTOUT (08/10/2026, demande de Mounir) =====
   « L'annulation de cœur sur les autres pages affiche un pop-up au milieu de l'écran au lieu d'un petit message comme sur
   le nouvel accueil : le mieux serait d'avoir le même principe partout. » Le mot d'un favori (retiré, ajouté par la ligne
   du menu de la fiche, enregistrement échoué) n'est plus la carte du centre : c'est la pilule de verre du plein écran — la
   même recette (jetons --verre*), la même taille, les mêmes durées (2 s ; un échec 3,2 s, annoncé role="alert") — posée EN
   HAUT : sous la barre de la page quand on en voit une, sinon à la place exacte de celle du plein écran (feuille 10). Elle
   ne bloque rien (pointer-events:none).
   AVEC UNE ACTION (« Annuler », sur la page des favoris) : 5 s, seule la pilule capte le toucher ; l'action part UNE fois,
   la pilule se retire aussitôt ; le clavier qui s'y pose suspend le minuteur (on n'enlève pas un bouton sous un doigt) ;
   quitter l'écran qui l'a dite (`lieu` change) la retire — on n'annule pas ce qu'on ne voit plus ; UN TOUCHER AILLEURS aussi
   (une autre annonce ouverte, un autre bouton : sinon elle resterait posée par-dessus le plein écran, à la place même de sa
   pilule, comme si l'annonce AFFICHÉE venait d'être retirée) — un défilement, lui, ne retire rien (pas de « click »).
   req = {id, texte, ton:""|"erreur", action:{label,faire}|null} ou null. onDone(id) ne nettoie que le mot encore affiché.
   Les autres confirmations de l'application gardent leur carte (LncToastHud) : aucun autre flux n'est touché. */
const MOT_MS=2000, MOT_ERREUR_MS=3200, MOT_ACTION_MS=5000, MOT_SORTIE_MS=240, MOT_REPRISE_MS=1500, MOT_SOUS_LA_BARRE=8, MOT_PLAFOND=.4;
const dureeDuMot=req=>(req&&req.action?MOT_ACTION_MS:(req&&req.ton==="erreur"?MOT_ERREUR_MS:MOT_MS));
// OÙ POSER LA PILULE. `bas` = le bas de la barre VUE en haut de l'écran (rien : une photo, une feuille, le plein écran) ;
// rend la distance au haut de l'écran, ou 0 : la feuille de style garde alors la place du plein écran. Une « barre » plus
// haute que 40 % de l'écran n'en est pas une. Pure, testée.
function sousLaBarre(bas,hauteur){
  const b=Number(bas), h=Number(hauteur);
  if(!(b>0)||!(h>0)||b>h*MOT_PLAFOND) return 0;
  return Math.round(b+MOT_SOUS_LA_BARRE);
}
// LA BARRE VUE : l'en-tête de l'application ou la barre d'une page — seulement si c'est bien elle que l'œil voit en haut
// (la fiche, une feuille, le plein écran passent DEVANT elle : on demande au navigateur ce qui est peint à 6 px du bord, là
// où la bannière d'un message entrant, centrée, ne va pas). Rien de vu ⇒ null.
//  (08/10, lot 2) la tête d'une page de filtres et le CHAMP de la page de recherche en sont aussi : « Historique de recherche
//  effacé · Annuler » s'y dit, et ne doit pas se poser sur le champ où l'on va écrire.
const MOT_BARRES="header.lanac-header,.lanac-mobile-page-top,.lanac-filter-head,.lanac-search-field";
function basDeLaBarre(doc){
  try{
    const d=doc||document; let bas=0;
    d.querySelectorAll(MOT_BARRES).forEach(b=>{
      const r=b.getBoundingClientRect();
      if(!(r.width>0&&r.height>0)||!(r.bottom>6)) return;
      const vu=d.elementFromPoint(r.left+6,r.bottom-6);
      if(vu&&(vu===b||b.contains(vu))) bas=Math.max(bas,r.bottom);
    });
    return bas||null;
  }catch(_){ return null; }
}
function LncMotCarte({req,onDone,lieu}){
  const ms=dureeDuMot(req);
  const [sort,setSort]=useState(false);
  const [sous]=useState(()=>sousLaBarre(basDeLaBarre(),typeof window!=="undefined"?window.innerHeight:0));
  const fini=useRef(false), minuteurs=useRef([]), lieu0=useRef(lieu), boite=useRef(null);
  const clore=()=>{ if(fini.current) return false; fini.current=true; onDone&&onDone(req.id); return true; };
  const armer=duree=>{
    minuteurs.current.forEach(clearTimeout);
    const reduit=typeof window!=="undefined"&&window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    minuteurs.current=[setTimeout(()=>setSort(true),Math.max(0,duree-(reduit?0:MOT_SORTIE_MS))),setTimeout(clore,duree)];
  };
  useEffect(()=>{ lanacBuzz(6); armer(ms); return()=>{ minuteurs.current.forEach(clearTimeout); }; },[]);
  useEffect(()=>{ if(req.action&&lieu!==lieu0.current) clore(); },[lieu]);
  useEffect(()=>{
    if(!req.action) return;
    const ailleurs=e=>{ const b=boite.current; if(b&&e&&e.target&&b.contains(e.target)) return; clore(); };
    document.addEventListener("click",ailleurs,true);
    return()=>document.removeEventListener("click",ailleurs,true);
  },[]);
  const agir=e=>{ e.stopPropagation(); if(!clore()) return; try{ req.action.faire(); }catch(_){} };
  return <div className="lnc-mot-wrap" style={sous?{"--lnc-mot-sous":sous+"px"}:undefined}>
    <div ref={boite} className={"lnc-mot"+(req.ton?" lnc-mot--"+req.ton:"")+(req.action?" lnc-mot--action":"")+(sort?" lnc-mot--sort":"")} data-lnc-mot={req.ton||"info"} role={req.ton==="erreur"?"alert":"status"} aria-atomic="true">
      <span className="lnc-mot-texte">{req.texte}</span>
      {req.action&&<button type="button" className="lnc-mot-action" data-lnc-mot-action="" onClick={agir} onFocus={()=>{ minuteurs.current.forEach(clearTimeout); setSort(false); }} onBlur={()=>{ if(!fini.current) armer(MOT_REPRISE_MS); }}>{req.action.label}</button>}
    </div>
  </div>;
}
function LncMot({req,onDone,lieu}){
  if(!req) return null;
  return <LncMotCarte key={req.id} req={req} onDone={onDone} lieu={lieu}/>;
}

/* ===== LncMsgToast — bannière GLASS « message entrant » (style iOS) =====
   Affichée quand un message arrive alors que l'utilisateur navigue AILLEURS dans
   l'app (jamais en messagerie — là, le fil est déjà sous ses yeux). Tap = ouvre la
   conversation ; ✕ = ferme ; auto-dismiss 4,5 s (minuteur ré-armé si un nouveau
   message de la MÊME conversation arrive : contenu remplacé + compteur, pas de
   ré-empilement — même philosophie de collapse que la bulle serveur). */
function LncMsgToastCard({req,onOpen,onClose,allUsers,lang}){
  const [closing,setClosing]=useState(false);
  const doneRef=useRef(false);
  const settle=fn=>{ if(doneRef.current) return; doneRef.current=true;
    setClosing(true);
    const reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(()=>{fn&&fn();},reduce?0:280);
  };
  useEffect(()=>{ lanacBuzz(6); },[]);
  useEffect(()=>{ // minuteur ré-armé à chaque bump (nouveau message même conversation)
    const t=setTimeout(()=>settle(onClose),4500);
    return()=>clearTimeout(t);
  },[req.bump]);
  const sender=(allUsers||[]).find(u=>u.id===req.fromId)||null;
  const excerpt=req.image&&!req.text
    ?tx("tax.photo_6365c1",lang)
    :(req.text||"");
  return <div className="lnc-msgtoast-wrap">
    <div className={"lnc-msgtoast"+(closing?" lnc-msgtoast--out":req.bump>0?" lnc-msgtoast--bump":"")}
         key={req.bump>0?"b"+req.bump:"in"}
         role="status" aria-live="polite" aria-atomic="true"
         onClick={()=>settle(onOpen)}>
      <span className="lnc-msgtoast-av">{sender?<Av u={sender} s={38}/>:<Av u={{name:"?",color:"var(--color-success)"}} s={38}/>}</span>
      <span className="lnc-msgtoast-body">
        <span className="lnc-msgtoast-top">
          <span className="lnc-msgtoast-name">{sender?.name||tx("tax.nouveau-message_bc4fa3",lang)}</span>
          {req.refTitle?<span className="lnc-msgtoast-ref">· {req.refTitle}</span>:null}
        </span>
        <span className="lnc-msgtoast-txt">{req.image&&!req.text&&<UiIcon name="camera" size={12} style={{marginRight:4}}/>}{excerpt}</span>
      </span>
      {req.count>1&&<span className="lnc-msgtoast-count">{req.count>9?"9+":req.count}</span>}
      <button className="lnc-msgtoast-x" aria-label={tx("tax.fermer_b89077",lang)}
              onClick={e=>{e.stopPropagation();settle(onClose);}}>
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M18 6 6 18"/><path d="M6 6l12 12"/></svg>
      </button>
    </div>
  </div>;
}
function LncMsgToast({req,onOpen,onClose,allUsers,lang}){
  if(!req) return null;
  return <LncMsgToastCard key={req.id} req={req} onOpen={onOpen} onClose={onClose} allUsers={allUsers} lang={lang}/>;
}

/* ===== LncUndoToast — « Annuler » AVEC UNE ÉCHÉANCE (annonce supprimée, historique effacé, brouillon enregistré) =====
   (08/10/2026, demande de Mounir : « le même principe partout ») C'ÉTAIT une carte au milieu de l'écran ; c'est la pilule de
   verre du petit mot (LncMot, plus haut : même recette, même place — sous la barre de la page quand on en voit une), avec
   son pictogramme, son bouton, et un trait qui s'écoule au bas du verre (5 s) : après lui, c'est fait.
   LE FLUX N'A PAS BOUGÉ : mêmes 5 s, mêmes deux minuteurs indépendants, onUndo/onExpire appelés UNE seule fois, « Annuler »
   synchrone ; l'app décide (restaurer vs commit destructif). À la différence du mot d'un favori, elle ne se retire NI au
   toucher ailleurs NI au changement d'écran : son échéance porte une suite — elle reste jusqu'à son terme ou à son bouton.
   SA PLACE SE MESURE APRÈS L'AFFICHAGE (dans l'effet), jamais pendant le rendu : elle est souvent dite pendant qu'un écran
   se ferme (la fiche de l'annonce supprimée, le formulaire du brouillon) — c'est la barre de l'écran qui RESTE qui compte ;
   et une mesure faite plus tôt forcerait la mise en page avant que l'application ne repose l'en-tête de la liste retrouvée
   (le retour tombait 46 px trop bas : A23). Tant qu'elle n'est pas mesurée la pilule n'est pas rendue : pas de saut. */
function LncUndoToastCard({req,onUndo,onExpire}){
  const [closing,setClosing]=useState(false);
  const [sous,setSous]=useState(null);          // null : pas encore mesurée (rien n'est rendu) ; 0 : aucune barre vue
  const doneRef=useRef(false);
  const settle=(fn)=>{if(doneRef.current)return;doneRef.current=true;fn&&fn();}; // action SYNCHRONE (fiable) — pas de setState différé
  useEffect(()=>{
    lanacBuzz(8);
    setSous(sousLaBarre(basDeLaBarre(),typeof window!=="undefined"?window.innerHeight:0));
    const reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t1=setTimeout(()=>setClosing(true),reduce?5000:4720); // amorce l'animation de sortie (visuelle)
    const t2=setTimeout(()=>settle(onExpire),5000);             // commit à 5 s (timer indépendant — pattern éprouvé du HUD)
    return()=>{clearTimeout(t1);clearTimeout(t2);};
  },[]);
  const onCancel=()=>{setClosing(true);settle(onUndo);}; // Annuler : restauration IMMÉDIATE (synchrone) → jamais de perte d'action
  return <div className="lnc-mot-wrap lnc-mot-wrap--delai" style={sous?{"--lnc-mot-sous":sous+"px"}:undefined}>
    {sous!==null&&<div className={"lnc-mot lnc-mot--action lnc-undo"+(closing?" lnc-mot--sort":"")} data-lnc-mot="delai" role="status" aria-live="polite" aria-atomic="true">
      <span className="lnc-undo-row">
        <span className="lnc-undo-ic"><svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13"/><path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg></span>
        <span className="lnc-mot-texte lnc-undo-msg">{req.msg}</span>
        <button type="button" className="lnc-mot-action lnc-undo-btn" onClick={onCancel}>{req.undoLabel}</button>
      </span>
      <span className="lnc-undo-timer" aria-hidden="true"><span className="lnc-undo-fill"/></span>
    </div>}
  </div>;
}
function LncUndoToast({req,onUndo,onExpire}){
  if(!req) return null;
  return <LncUndoToastCard key={req.id} req={req} onUndo={onUndo} onExpire={onExpire}/>;
}

/* ===== LncToastHud — LE MESSAGE DE L'APPLICATION (confirmation, information, refus, échec : tout ce que dit notify()) =====
   (08/10/2026, lot 3, demande de Mounir : « une carte au centre s'affiche […] il faut absolument le passer en pilule […] le même
   principe moderne et premium partout ») C'ÉTAIT une carte au milieu de l'écran, avec un grand pictogramme, 2 s quelle que
   soit la phrase ; c'est la pilule de verre du petit mot (LncMot), EN HAUT — sous la barre de la page quand on en voit une.
   notify(msg,color) et ses ~200 appelants n'ont pas changé : req = {msg, color, id} ou null, le ton se lit dans la couleur.
   · un succès porte une coche fine, tracée ; une erreur le point rouge du petit mot, et s'ANNONCE (role="alert") ;
   · LA DURÉE SUIT LA LONGUEUR (dureeDuMessage) : 2 s pour un mot ; une phrase reste le temps d'être lue (un refus explique —
     jusqu'à 128 caractères aujourd'hui : 6 s), une erreur 1,2 s de plus ; jamais plus de 6,5 s ;
   · elle ne capte AUCUN toucher ; sa place se mesure dans l'effet, jamais pendant le rendu (un message est souvent dit
     pendant qu'un écran se ferme ; et une mesure plus tôt forcerait la mise en page avant la repose de l'en-tête — A23).
   · UNE SEULE COCHE : bien des messages commencent déjà par « ✓ » dans leur texte — ce « ✓ » reste dans le document (lu
     mot pour mot par les scénarios et les lecteurs d'écran) mais ne se voit plus ; la coche tracée le dit, quel que soit
     le ton (messageCoche).
   Le texte garde la classe .lnc-hud-msg : c'est par elle que les scénarios lisent ce que l'application dit.
   onDone ne nettoie que si le message affiché est toujours le même id (anti-course si un nouveau arrive). */
const LNC_TOAST_ICONS={
  success:<svg className="lnc-mot-coche" aria-hidden="true" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path className="lnc-hud-check" d="M20 6 9 17l-5-5"/></svg>
};
function lncToastTone(color){
  const c=String(color||"");
  if(c.indexOf("error")>=0) return "error";
  if(c.indexOf("success")>=0) return "success";
  return "info";
}
const MSG_SEUIL=38, MSG_PAR_CARACTERE_MS=45, MSG_PLAFOND_MS=6500;
//  COMBIEN DE TEMPS LE MESSAGE RESTE : la durée du petit mot (2 s ; une erreur 3,2 s) jusqu'à 38 caractères, puis 45 ms par
//  caractère de plus (une lecture posée), sans dépasser 6,5 s. Pure, testée.
function dureeDuMessage(msg,ton){
  const n=String(msg==null?"":msg).length, base=ton==="error"?MOT_ERREUR_MS:MOT_MS;
  return Math.min(MSG_PLAFOND_MS,base+Math.max(0,n-MSG_SEUIL)*MSG_PAR_CARACTERE_MS);
}
//  LE « ✓ » QUI OUVRE UN MESSAGE : rend [le début à taire, le reste] — ou null (pas un texte, pas de coche en tête). Pure, testée.
function messageCoche(msg){
  if(typeof msg!=="string") return null;
  const m=/^\s*[✓✔]\s*/.exec(msg);
  return m?[m[0],msg.slice(m[0].length)]:null;
}
function LncToastCard({req,onDone}){
  const [closing,setClosing]=useState(false);
  const [sous,setSous]=useState(null);          // null : pas encore mesurée (rien n'est rendu) ; 0 : aucune barre vue
  const doneRef=useRef(false);
  const tone=lncToastTone(req.color), coche=messageCoche(req.msg);
  useEffect(()=>{
    lanacBuzz(6);
    setSous(sousLaBarre(basDeLaBarre(),typeof window!=="undefined"?window.innerHeight:0));
    const reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ms=dureeDuMessage(req.msg,tone);
    const t1=setTimeout(()=>setClosing(true),Math.max(0,ms-(reduce?0:MOT_SORTIE_MS)));
    const t2=setTimeout(()=>{if(!doneRef.current){doneRef.current=true;onDone&&onDone(req.id);}},ms);
    return()=>{clearTimeout(t1);clearTimeout(t2);};
  },[]);
  return <div className="lnc-mot-wrap lnc-mot-wrap--message" style={sous?{"--lnc-mot-sous":sous+"px"}:undefined}>
    {sous!==null&&<div className={"lnc-mot lnc-mot--message"+(tone==="error"?" lnc-mot--erreur":tone==="success"?" lnc-mot--succes":"")+(closing?" lnc-mot--sort":"")} data-lnc-mot="message" data-ton={tone} role={tone==="error"?"alert":"status"} aria-atomic="true">
      {(tone==="success"||coche)&&LNC_TOAST_ICONS.success}
      <span className="lnc-mot-texte lnc-hud-msg">{coche?<span className="lnc-mot-muet">{coche[0]}</span>:null}{coche?coche[1]:req.msg}</span>
    </div>}
  </div>;
}
function LncToastHud({req,onDone}){
  if(!req) return null;
  return <LncToastCard key={req.id} req={req} onDone={onDone}/>;
}

/* ===== LncHistoryClear — « Tout effacer » l'historique de recherche (26/09/2026, C11 (i)) =====
   Une action DISCRÈTE, jamais un cri : texte secondaire et icône fine (la corbeille du bandeau « Annuler » qu'elle
   déclenche), pilule au survol, 44 px de cible au toucher, anneau au clavier. Pas de boîte de confirmation : l'effacement
   se rattrape d'un tap (« Annuler », 5 s). Le nom accessible CONTIENT le texte visible (WCAG 2.5.3) et dit de quoi il s'agit.
   `onMouseDown` retenu : le champ de recherche garde le focus (la liste ne se referme pas sous le doigt). */
function LncHistoryClear({onClear,label,ariaLabel}){
  return <button type="button" className="lnc-history-clear" onMouseDown={e=>e.preventDefault()} onClick={onClear} aria-label={ariaLabel}>
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13"/><path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg>
    <span>{label}</span>
  </button>;
}

/* (09/10/2026) « Nouvelle version · Actualiser » (LncUpdateToast, 29/08 → 08/10) N'EXISTE PLUS : la mise à jour se fait seule, à un
   moment calme, sans rien demander (hooks/useMiseAJour). Il ne reste que trois pilules : l'échéance, le message, le mot. */

// Placeholder de chargement (skeleton) imitant une carte annonce : image + 2 lignes + prix.
function SkeletonCard(){
  return <div aria-hidden="true" style={{background:"var(--color-surface)",border:"1px solid var(--color-border)",borderRadius:14,overflow:"hidden"}}>
    <div className="lanac-sk" style={{height:140}}/>
    <div style={{padding:"10px 12px"}}>
      <div className="lanac-sk" style={{height:12,width:"82%",borderRadius:4,marginBottom:8}}/>
      <div className="lanac-sk" style={{height:12,width:"52%",borderRadius:4,marginBottom:11}}/>
      <div className="lanac-sk" style={{height:16,width:"42%",borderRadius:4}}/>
    </div>
  </div>;
}


// ── L'ATTERRISSAGE DU L (10/09/2026) ──────────────────────────────────────────────────────
//  Rendu UNE fois, dans le MÊME commit que le premier écran de l'app, à la place exacte de
//  l'écran de boot (même balisage, même géométrie : fixed inset 0, porté dans <body> par portail
//  pour qu'aucun ancêtre transformé ne casse le repère). Rôle : faire du passage splash → app un
//  GESTE et non une coupure. La tuile L glisse et rétrécit jusqu'au logo de l'en-tête
//  (.lanac-logo-mark) pendant que le voile se fond ; puis le nœud est RETIRÉ (onDone → démonté),
//  jamais seulement masqué. Sans cible visible (écran de connexion, vue sans logo) : fondu 0,3 s.
//  prefers-reduced-motion : rien n'est rendu (l'app apparaît d'un coup, état final).
//  INVARIANTS : jamais cliquable (pointer-events:none), aria-hidden, BORNÉ dans le temps
//  (1 400 ms au pire — même onglet caché, où transitionend ne vient jamais), 0 réseau,
//  0 état de module. Il ne peut retarder aucune logique : l'app est déjà montée dessous.
//  ⚠️ La mesure se fait sur la tuile AU REPOS (.lncb-land coupe ses animations) : un décalage
//  animé au moment de la lecture ferait sauter le départ.
function SplashLanding({onDone}){
  const ref=useRef(null);
  const doneRef=useRef(onDone); doneRef.current=onDone;
  // ⭐ 27/09/2026 — LE BALISAGE EST CALCULÉ UNE FOIS, AU MONTAGE. dangerouslySetInnerHTML réécrit le nœud dès que sa
  //  chaîne change : un re-rendu de l'app pendant le vol (il y en a des dizaines — les données arrivent) avec un salut
  //  différent remplaçait la tuile EN PLEIN VOL, détachée de son transform (elle repartait du centre). Le salut est figé
  //  (shell · __lncSalut) ; le balisage l'est aussi, par construction.
  const [html]=useState(()=>'<div class="lncb-veil"></div>'+lncbBootHtml({greet:true}));
  useEffect(()=>{
    const wrap=ref.current;
    let done=false, raf=0; const timers=[];
    // `debrancher` retire les écouteurs de recalage posés plus bas. Déclaré ICI pour que finish()
    //  et le démontage le voient : un écouteur de `resize` survivrait au nœud retiré.
    let debrancher=()=>{};
    const finish=()=>{ if(done) return; done=true; debrancher(); timers.forEach(clearTimeout); if(raf) cancelAnimationFrame(raf); const cb=doneRef.current; if(cb) cb(); };
    let reduce=false; try{ reduce=!!window.matchMedia("(prefers-reduced-motion: reduce)").matches; }catch(_){}
    const tile=wrap&&wrap.querySelector(".lncb-tile");
    if(!wrap||!tile||reduce){ finish(); return undefined; }
    const target=document.querySelector(".lanac-logo-mark");
    const tr=target?target.getBoundingClientRect():null;
    const vis=!!tr&&tr.width>=8&&tr.height>=8&&tr.top>=0&&tr.left>=0&&tr.bottom<=window.innerHeight&&tr.right<=window.innerWidth;
    if(vis){
      // ⚠️⚠️ ON FIGE LE POINT DE DÉPART AVANT DE VISER (12/09/2026, défaut signalé : « le L se
      //  rétracte À CÔTÉ du logo »). Le voile est `fixed; inset:0` avec `align-items:center` et
      //  la scène porte `translateY(-4vh)` : la position de départ de la tuile dépend DEUX FOIS
      //  de la hauteur du viewport, alors que le translate calculé est un delta en pixels FIGÉ.
      //  Sur un téléphone, la barre d'adresse se replie PENDANT le chargement — 60 à 110 px — et
      //  la tuile atterrit à 0,46 × Δ du logo (centrage Δ/2 moins 4 % de Δ). MESURÉ : 41,40 px
      //  d'écart pour 90 px de repli, contre 41,4 px prédits. Invisible en headless, où le
      //  viewport ne bouge jamais — d'où un S85 vert sur un défaut réel.
      //  On épingle donc la scène LÀ OÙ L'ŒIL LA VOIT, en coordonnées de viewport : même
      //  rectangle au pixel (aucun changement visuel), et plus aucune dépendance à la hauteur.
      //  Le voile n'a ni transform ni filtre ni will-change : `fixed` y vaut bien « viewport ».
      const scene=wrap.querySelector(".lncb-scene");
      if(scene){
        const sr=scene.getBoundingClientRect();
        scene.style.position="fixed"; scene.style.margin="0"; scene.style.transform="none";
        scene.style.left=`${sr.left.toFixed(2)}px`; scene.style.top=`${sr.top.toFixed(2)}px`;
        scene.style.width=`${sr.width.toFixed(2)}px`; scene.style.height=`${sr.height.toFixed(2)}px`;
      }
      const t0=tile.getBoundingClientRect(); // APRÈS l'épinglage : le départ ne bougera plus
      // On VISE la cible à chaque fois plutôt qu'une fois pour toutes : si l'en-tête grandit
      //  pendant le vol (rangées de recherche/filtres qui arrivent avec les données), la tuile
      //  se recale. Une transition CSS repart de sa valeur courante : le recalage est fluide.
      const viser=()=>{
        const c=target.getBoundingClientRect();
        tile.style.transform=`translate(${(c.left-t0.left).toFixed(2)}px,${(c.top-t0.top).toFixed(2)}px) scale(${(c.width/t0.width).toFixed(4)})`;
      };
      const reviser=()=>{ if(!done) viser(); };
      window.addEventListener("resize",reviser);
      let vv=null; try{ vv=window.visualViewport||null; if(vv) vv.addEventListener("resize",reviser); }catch(_){}
      debrancher=()=>{ window.removeEventListener("resize",reviser); try{ if(vv) vv.removeEventListener("resize",reviser); }catch(_){} };
      tile.addEventListener("transitionend",e=>{ if(e.target===tile&&e.propertyName==="transform") finish(); });
      raf=requestAnimationFrame(()=>{ raf=0; wrap.classList.add("lncb-go"); viser(); });
      timers.push(setTimeout(finish,1400));
    }else{
      wrap.classList.add("lncb-fade");
      raf=requestAnimationFrame(()=>{ raf=0; wrap.classList.add("lncb-go"); });
      timers.push(setTimeout(finish,380));
    }
    // ⚠️ `debrancher()` AUSSI au démontage : un écouteur de `resize` survit au nœud retiré et
    //  rappellerait viser() sur une tuile détachée (lecture inutile à chaque rotation d'écran).
    return ()=>{ done=true; debrancher(); timers.forEach(clearTimeout); if(raf) cancelAnimationFrame(raf); };
  },[]);
  return ReactDOM.createPortal(<div ref={ref} className="lncb-wrap lncb-land" aria-hidden="true" dangerouslySetInnerHTML={{__html:html}}/>,document.body);
}
export { AppSheet, Btn, Fld, IconBtn, LNC_DIALOG_ICONS, LNC_TOAST_ICONS, LncDialogCard, LncDialogHost, LncHistoryClear, MobilePageHeader, LncMot, LncMotCarte, LncMsgToast, LncMsgToastCard, LncToastCard, LncToastHud, LncUndoToast, LncUndoToastCard, dureeDuMessage, messageCoche, LncVideo, Pill, SkeletonCard, VideoPlayer, __lncDialogDefaults, __lncDialogPush, __lncDialogSeq, __lncPlain, __lncSheetLocks, __lncSheetPadPrev, __lncSheetStack, lncAlert, lncConfirm, lncSheetLock, lncSheetUnlock, lncToastTone, SplashLanding, MOT_MS, MOT_ERREUR_MS, MOT_ACTION_MS, VIDEO_REESSAIS, VIDEO_REESSAI_MS, dureeDuMot, sousLaBarre, basDeLaBarre };
